// Website page-view beacon. Without this the analytics tab's web metrics
// (web_page_views / web_unique_visitors) never see a single row — the backend
// only knows about visits the site reports.
//
// Anonymous by construction: the visitorId is a random UUID minted into
// localStorage, and nothing else is sent. The endpoint is public and always
// answers 202, so a failure here must never surface in the page.
import { useEffect, useRef } from 'react';
import { BACKEND_URL } from '../config';

const STORAGE_KEY = 'br_visitor_id';

// crypto.randomUUID needs a secure context (https or localhost); fall back to a
// UUID-shaped random string so a plain-http origin still reports visits.
const randomUuid = (): string => {
	const c: Crypto | undefined = typeof crypto !== 'undefined' ? crypto : undefined;
	if (c?.randomUUID) return c.randomUUID();
	const bytes = new Uint8Array(16);
	if (c?.getRandomValues) {
		c.getRandomValues(bytes);
	} else {
		for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
	}
	bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
	bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 1
	const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
	return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/** The anonymous visitor UUID, minted on first use; null if storage is blocked. */
export const getVisitorId = (): string | null => {
	try {
		let id = localStorage.getItem(STORAGE_KEY);
		if (!id) {
			id = randomUuid();
			localStorage.setItem(STORAGE_KEY, id);
		}
		return id;
	} catch {
		// Storage blocked (private mode / cookie settings) — skip the beacon
		// rather than mint a new id on every view and inflate unique visitors.
		return null;
	}
};

/** Fire-and-forget page view. Never throws, never surfaces an error. */
export const sendPageView = (path: string): void => {
	const visitorId = getVisitorId();
	if (!visitorId) return;
	try {
		fetch(`${BACKEND_URL}/analytics/beacon`, {
			method: 'POST',
			keepalive: true,
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ visitorId, path }),
		}).catch(() => {});
	} catch {
		/* ignore */
	}
};

/** Reports the current route on first load and on every route change. */
export const usePageViewBeacon = (path: string): void => {
	const lastSent = useRef<string | null>(null);
	useEffect(() => {
		if (lastSent.current === path) return;
		lastSent.current = path;
		sendPageView(path);
	}, [path]);
};
